import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { ArchivedContent, Item } from '../api/types.js';
import { ArchivedArticle } from './archived-article.js';
import { TagEditor } from './tag-editor.js';

/**
 * The item reader (M1.16.2).
 *
 * ## The markup is the server's, sanitised before it was stored
 *
 * Feed content is sanitised at ingest (M1.2.7) — allowlisted elements and
 * attributes, no scripts, no event handlers, no `javascript:` URLs — so what
 * arrives here has already been through it. Sanitising again in the browser
 * would be a second implementation of a security boundary, which is how the
 * two drift apart and one of them becomes the weaker one everybody forgets.
 *
 * `dangerouslySetInnerHTML` is therefore deliberate and narrow: it renders
 * *this* field and nothing else. The **archive** is never inlined here: it is
 * a whole page from a publisher we do not control, so it is framed from a
 * separate origin instead ([D4](../../../Design-Decisions.md), M3.8.2,
 * M3.11.1). See `archived-article.tsx` for why the distinction is not
 * fussiness.
 *
 * ## Links leave
 *
 * `target="_blank"` with `rel="noopener noreferrer"`. Without `noopener` the
 * opened page gets a handle on this one through `window.opener`; the
 * sanitiser adds the attribute server-side too, and this is the second half of
 * the same rule for links we render ourselves.
 */

export interface ItemViewProps {
  readonly item: Item | undefined;
  readonly onToggleRead: (read: boolean) => void;
  readonly onOpen: () => void;
  /** Fetches the signed URL for an archived body (M3.8.1). */
  readonly fetchArchivedContent?: (itemId: string) => Promise<ArchivedContent>;
  /** Reports an archive open with the item's age in days (M3.11.2). */
  readonly onArchiveOpened?: (ageDays: number) => void;
  /** So `space` can ask whether there is more article to scroll. */
  readonly scrollRef: React.RefObject<HTMLDivElement | null>;
  /** Stars and tags (M4.2.3). Absent, the controls are not shown. */
  readonly onToggleStar?: (() => void) | undefined;
  readonly onSetTags?: ((tags: readonly string[]) => void) | undefined;
  readonly knownTags?: readonly string[] | undefined;
}

export function ItemView(props: ItemViewProps): ReactNode {
  const { item } = props;
  const lastMarked = useRef<string | undefined>(undefined);
  const [showArchive, setShowArchive] = useState(false);

  /*
   * The toggle resets per item. Leaving it on would open the next item
   * straight into an archive view that may not exist for it, and the reader
   * would meet "no archived copy" as if it were the normal state.
   */
  useEffect(() => {
    setShowArchive(false);
  }, [item?.id]);

  /*
   * Marks read on open (M1.16.2), once per item.
   *
   * Guarded by the id rather than by a boolean: without it, marking an item
   * *unread* while it is open would immediately mark it read again, and the
   * explicit control would appear not to work.
   */
  useEffect(() => {
    if (item === undefined || item.read || lastMarked.current === item.id) {
      return;
    }
    lastMarked.current = item.id;
    props.onToggleRead(true);
  }, [item, props]);

  if (item === undefined) {
    return (
      <article className="reader reader--empty">
        <p className="state__text">Select an item to read it.</p>
      </article>
    );
  }

  return (
    <article className="reader" ref={props.scrollRef} tabIndex={-1}>
      <header className="reader__header">
        <h1 className="reader__title">
          {item.link === null ? (
            item.title
          ) : (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={props.onOpen}
            >
              {item.title}
            </a>
          )}
        </h1>
        <p className="reader__meta">
          {item.feedTitle ?? 'Unknown feed'}
          {item.author === null ? '' : ` · ${item.author}`}
          {' · '}
          <time dateTime={item.publishedAt}>{formatDate(item.publishedAt)}</time>
        </p>

        <ArchiveStatus extraction={item.extraction} />
        <div className="reader__actions">
          <button
            className="button button--small"
            onClick={() => {
              props.onToggleRead(!item.read);
            }}
          >
            {item.read ? 'Mark unread' : 'Mark read'}
          </button>
          {props.onToggleStar !== undefined && (
            <button
              className={`button button--small${item.starred ? ' is-starred' : ''}`}
              aria-pressed={item.starred}
              onClick={props.onToggleStar}
            >
              {item.starred ? '★ Starred' : '☆ Star'}
            </button>
          )}
          {props.fetchArchivedContent !== undefined && item.link !== null && (
            <button
              className="button button--small"
              onClick={() => {
                setShowArchive((shown) => !shown);
              }}
              aria-pressed={showArchive}
              /*
               * Never disabled by status. A reader whose article failed
               * extraction can still ask, and the archived view explains what
               * happened far better than a greyed-out control does.
               */
              /*
               * Offered even when `hasArchivedContent` is false. The flag is
               * derived from the item's status, which can lag the artifact by
               * a moment, and the archived view explains "no copy" far better
               * than a missing button does — a control that is sometimes
               * there is harder to learn than one that sometimes says no.
               */
            >
              {showArchive ? 'Show summary' : 'Read full article'}
            </button>
          )}
          {item.link !== null && (
            <a
              className="button button--small"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={props.onOpen}
            >
              Open original
            </a>
          )}
        </div>
        {props.onSetTags !== undefined && (
          <TagEditor
            tags={item.tags}
            known={props.knownTags ?? []}
            onChange={props.onSetTags}
          />
        )}
      </header>

      {showArchive && item.link !== null && props.fetchArchivedContent !== undefined ? (
        <ArchivedArticle
          item={item}
          fetchContent={props.fetchArchivedContent}
          {...(props.onArchiveOpened === undefined
            ? {}
            : { onOpened: props.onArchiveOpened })}
        />
      ) : item.summaryHtml === null || item.summaryHtml === '' ? (
        <p className="reader__empty-body">
          This feed publishes titles only. Open the original to read it.
        </p>
      ) : (
        <div
          className="reader__body"
          // Sanitised server-side at ingest (M1.2.7). See the note above.
          dangerouslySetInnerHTML={{ __html: item.summaryHtml }}
        />
      )}
    </article>
  );
}

/**
 * What the archive did with this item (M3.9.2).
 *
 * **Presenting a shell as an archive is the most damaging bug available in
 * this product** — it converts a known gap into a silent one, discovered
 * years later when the original is gone. So the states that are *not* `ok`
 * are shown, in words, rather than being absent and leaving the reader to
 * assume the best.
 *
 * `ok` shows nothing at all, deliberately. A badge on every successful
 * article would be noise that teaches people to stop reading badges, and the
 * archived view is its own evidence that it worked.
 *
 * `escalated` reads as pending rather than as failure, because it is: the
 * content exists and a browser render may yet get it (D6).
 */
function ArchiveStatus({ extraction }: { readonly extraction: string }): ReactNode {
  if (extraction === 'ok' || extraction === 'none') {
    return null;
  }

  const described: Record<string, { readonly tone: string; readonly text: string }> = {
    pending: { tone: 'pending', text: 'Fetching the full article…' },
    escalated: { tone: 'pending', text: 'Still working on the full article…' },
    original_only: {
      tone: 'partial',
      text: 'No archived copy — the publisher asked us not to keep one, or there was no article to extract.',
    },
    failed: {
      tone: 'failed',
      text: 'We could not archive this one. The original is still available.',
    },
  };

  const state = described[extraction];

  if (state === undefined) {
    return null;
  }

  return (
    <p
      className={`archive-status archive-status--${state.tone}`}
      role={state.tone === 'pending' ? 'status' : undefined}
    >
      {state.text}
    </p>
  );
}

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};
